import React from 'react';
import { ShieldAlert, Scale, AlertTriangle } from 'lucide-react';
import type { WithdrawalRequest } from '../../../types';
import { cn } from '../../../utils';
import {
  getWithdrawalReviewKindClassName,
  getWithdrawalReviewSummary,
  type WithdrawalReviewKind,
} from '../../../utils/withdrawalFlagDisplay';

type Variant = 'compact' | 'banner';

type Props = {
  withdrawal: WithdrawalRequest;
  variant?: Variant;
  className?: string;
  onViewFullAnalysis?: () => void;
};

function ReviewIcon({ kind }: { kind: WithdrawalReviewKind }) {
  if (kind === 'security') return <ShieldAlert className="h-4 w-4 shrink-0" />;
  if (kind === 'amount') return <Scale className="h-4 w-4 shrink-0" />;
  return <AlertTriangle className="h-4 w-4 shrink-0" />;
}

export function WithdrawalReviewIndicator({
  withdrawal,
  variant = 'compact',
  className,
  onViewFullAnalysis,
}: Props) {
  const summary = getWithdrawalReviewSummary(withdrawal);
  if (!summary) return null;

  if (variant === 'compact') {
    return (
      <div className={cn('space-y-1', className)}>
        <span
          className={cn(
            'inline-flex items-center gap-1 rounded-full border px-2 py-0.5 text-xs font-medium',
            getWithdrawalReviewKindClassName(summary.kind),
          )}
          title={summary.reasons.join('\n')}
        >
          <ReviewIcon kind={summary.kind} />
          {summary.title}
        </span>
        <p className="text-xs text-muted-foreground leading-snug max-w-[14rem]">
          {summary.shortReason}
          {summary.reasons.length > 1 ? ` (+${summary.reasons.length - 1} more)` : null}
        </p>
      </div>
    );
  }

  const bannerStyles =
    summary.kind === 'security'
      ? 'border-orange-200 bg-orange-50 text-orange-950'
      : summary.kind === 'amount'
        ? 'border-blue-200 bg-blue-50 text-blue-950'
        : 'border-amber-200 bg-amber-50 text-amber-950';

  return (
    <div
      role="alert"
      className={cn('rounded-lg border px-4 py-3 text-sm flex gap-3', bannerStyles, className)}
    >
      <ReviewIcon kind={summary.kind} />
      <div className="min-w-0 flex-1 space-y-2">
        <div>
          <p className="font-semibold">{summary.title}</p>
          <p className="mt-1 leading-relaxed">
            This withdrawal was paused for review. Please check the reason below before approving
            or rejecting.
          </p>
        </div>
        <ul className="space-y-1">
          {summary.reasons.map((reason) => (
            <li key={reason} className="flex items-start gap-2 leading-relaxed">
              <span className="mt-1.5 h-1.5 w-1.5 shrink-0 rounded-full bg-current opacity-70" />
              <span>{reason}</span>
            </li>
          ))}
        </ul>
        {summary.kind === 'amount' ? (
          <p className="text-xs opacity-90">
            This is a routine check for large withdrawals. It is not the same as a fraud alert.
          </p>
        ) : null}
        {onViewFullAnalysis ? (
          <button
            type="button"
            onClick={onViewFullAnalysis}
            className="text-sm font-medium underline underline-offset-2 hover:opacity-80"
          >
            View full review details
          </button>
        ) : null}
      </div>
    </div>
  );
}

export default WithdrawalReviewIndicator;
